import { useMobile } from "@with-nx/hooks-n-helpers";
import { DesignedButton, DesignedCard, Rule } from "@with-nx/simple-ui/atoms";
import { Col, Row } from "antd";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Box } from "simple-effing-primitive-layout";

import { TabbedAuthForm } from "../auth/TabbedAuthForm";
import { useAuthForm } from "../auth/useAuthForm";

const BlurryBackground = ({ children, style = {} }) => (
  <Box
    parse="p:absolute t:0 l:0 w:100% h:100% i:99"
    style={{
      backgroundImage:
        "linear-gradient(0deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.5) 75%, rgba(0, 0, 0, 0) 100%)",
      backdropFilter: "blur(5px)",
      WebkitBackdropFilter: "blur(5px)",
      ...style,
    }}
  >
    {children}
  </Box>
);

export const RestrictedPreviewTop = ({
  thumbnails,
  customTitle,
  showTitle,
}) => {
  const mobile = useMobile();
  const router = useRouter();

  return (
    <Box top={24} css="container">
      <Box parse={mobile ? "pa:24 d:flex fd:column a:center" : ""}>
        <Rule bottom={24} parse="d:flex">
          {thumbnails && thumbnails?.length ? (
            <ThumbnailsPreview thumbnails={thumbnails} />
          ) : (
            <img
              loading="lazy"
              src="/assets/icons/locked_previews.png"
              alt="locked previews icon"
            />
          )}
        </Rule>

        <Rule
          rule="hl"
          color="var(--font1)"
          top={16}
          parse="c:?font1 d:block mb:12"
        >
          {customTitle || "Unlock Scene previews"}
        </Rule>

        <Rule rule="ht" color="var(--font2)" top={24} parse="d:block mb:12">
          {showTitle ? (
            <>
              Here is a sample of our animated {showTitle} Scenic Projections.
              Sign in below (or create an account), to preview the
              script-accurate show package and see how you can bring your stage
              to life! Question?{" "}
              <a
                style={{ color: "var(--primary)" }}
                href={`/contact/start?referrer=${router.asPath}`}
              >
                Click here to request information
              </a>
              .
            </>
          ) : (
            "Welcome to Broadway Media! We&apos;re thrilled to offer you an exclusive sneak peek into our captivating scenes. To access these scene previews and embark on a journey into the world of entertainment, simply log in or create an account."
          )}
        </Rule>
      </Box>
    </Box>
  );
};

export const RestrictedPreviewBottom = ({
  onTabChange,
}: {
  onTabChange?: (tab: string) => void;
}) => {
  const { _shouldNotRedirectLogin } = useAuthForm();

  useEffect(() => {
    _shouldNotRedirectLogin(true);
  }, []);

  return (
    <Box top={48} css="container">
      <Row gutter={[36, 0]} justify="center">
        <Col xs={24}>
          <Rule
            rule="hl"
            color="var(--font1)"
            top={0}
            parse="c:?font1 d:block mb:0 ta:center"
            align="center"
          >
            Unlock All Previews
          </Rule>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <TabbedAuthForm top={0} onTabChange={onTabChange} />
        </Col>
      </Row>
    </Box>
  );
};

const BlockedPreview = ({ thumbnails, customTitle }) => {
  const router = useRouter();

  const getStartedLink = `/contact/start?mode=get-started&referrer=${router.asPath}`;

  return (
    <Box parse="w:100% d:flex a:center j:center pt:24 pb:24 i:2 p:sticky t:0 l:0 r:unset b:unset fd:column">
      <Col xs={24} sm={24} md={12}>
        <Rule bottom={24} parse="d:flex j:center">
          {thumbnails && thumbnails?.length ? (
            <ThumbnailsPreview thumbnails={thumbnails} />
          ) : (
            <img
              loading="lazy"
              src="/assets/icons/locked_previews.png"
              alt="locked previews icon"
            />
          )}
        </Rule>

        <Rule
          rule="dt"
          color="var(--font1)"
          top={16}
          bottom={24}
          parse="!hl c:?font1 d:block mb:12 ta:center"
        >
          {customTitle || "Unlock Scene previews"}
        </Rule>

        <Rule
          rule="ls"
          color="var(--font3)"
          display="block"
          top={24}
          bottom={24}
          style={{
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 4,
            lineClamp: 4,
            WebkitBoxOrient: "vertical",
          }}
          align="center"
          parse="!ll d:block mb:16 ta:center"
        >
          It seems you&apos;ve reached your monthly content limit of 10
          previews. But don&apos;t worry! To unlock more of Broadway
          Media&apos;s exclusive scene previews and immerse yourself in the
          world of entertainment, please reach out to your production
          specialist.
        </Rule>

        <Box parse="ta:center">
          <DesignedButton
            size="small"
            theme="primary"
            label="Get Started"
            properties={{ left: 5, right: 5 }}
            href={getStartedLink}
          />

          <DesignedButton
            size="small"
            theme="tetriary-white"
            label="Schedule a Call"
            properties={{ left: 5, right: 5 }}
            href="/contact/calendar"
          />
        </Box>
      </Col>
    </Box>
  );
};

const ThumbnailsPreview = ({ thumbnails }) => {
  const _thumbnails = !thumbnails ? [] : thumbnails?.slice(0, 3);

  return (
    <Box parse="p:relative w:180 h:140">
      {_thumbnails.map((thumbnail, index) => {
        return (
          <Box
            parse="w:140 p:absolute b:0"
            mode="position"
            layer={index + 1}
            left={index * 12 + 10}
            bottom={-1 * index * 16 + 30}
            key={index}
            style={{
              transform: `rotate(-${(2 - index) * 15}deg)`,
              boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
            }}
          >
            <DesignedCard image={thumbnail} size="16x9" watermark={true} />
          </Box>
        );
      })}
    </Box>
  );
};

export const LockedScenePreviews = ({
  restricted,
  render,
  thumbnails,
  customTitle,
  showTitle,
  previewsCount = 0,
}) => {
  const [tab, _tab] = useState("Sign In");
  const blocked = !restricted;

  if (blocked) {
    return (
      <Box parse={`p:relative pt:24 pb:24 mh:450`}>
        <BlurryBackground>
          <BlockedPreview thumbnails={thumbnails} customTitle={customTitle} />
        </BlurryBackground>

        {render({})}
      </Box>
    );
  }

  return (
    <Box parse={`p:relative pt:24 pb:24 mh:${tab === "Sign Up" ? 850 : 450}`}>
      <RestrictedPreviewTop
        thumbnails={thumbnails}
        customTitle={customTitle}
        showTitle={showTitle}
      />

      <Box parse="pt:48 pb:48">
        {render({ onlyPreviews: true, beforeIndex: 6 })}
      </Box>

      {previewsCount >= 6 ? (
        <Box
          parse={`p:relative pt:24 mt:24 pb:24 mh:${
            tab === "Sign Up" ? 850 : 450
          }`}
        >
          <BlurryBackground>
            <RestrictedPreviewBottom onTabChange={(t) => _tab(t)} />
          </BlurryBackground>
          {render({ onlyPreviews: true, afterIndex: 6 })}
        </Box>
      ) : undefined}
    </Box>
  );
};
